import axios from "axios";
import config from "../config";

// ─── Constants ────────────────────────────────────────────────────────────────
const BASE_URL = `${config.host}/api/3/action`;

// ─── Axios Instance ───────────────────────────────────────────────────────────
const ckanApi = axios.create({ baseURL: BASE_URL });

// ─── Helpers ──────────────────────────────────────────────────────────────────
const normalizeDataset = (pkg) => ({
    name:              pkg.name,
    title:             pkg.title                        ?? "-",
    author:            pkg.author ?? pkg.organization?.title ?? "-",
    organization:      pkg.organization                 ?? null,
    metadata_modified: pkg.metadata_modified            ?? null,
    resources:         pkg.resources                    ?? [],
    source:            "CKAN",
});

// ─── Service Functions ────────────────────────────────────────────────────────

/**
 * Ambil total jumlah dataset CKAN.
 * rows=0 agar tidak download semua data, cukup ambil count-nya.
 * @returns {number}
 */
export const getDatasetCount = async () => {
    try {
        const res = await ckanApi.get("/package_search?rows=0");
        return res.data?.result?.count ?? 0;
    } catch (error) {
        console.error("getDatasetCount:", error.message);
        return 0;
    }
};

/**
 * Ambil semua organisasi CKAN dengan detail lengkap.
 * CKAN server ini membatasi max 25 item per request (limit diabaikan).
 * Gunakan offset loop untuk fetch semua data secara bertahap.
 * @returns {Array}
 */
export const getOrganizations = async () => {
    try {
        const allOrgs = [];
        let offset    = 0;
        const step    = 25; // max per request pada CKAN server ini

        while (true) {
            const res = await ckanApi.get(
                `/organization_list?all_fields=true&include_extras=true&offset=${offset}`
            );
            const batch = res.data?.result ?? [];
            allOrgs.push(...batch);

            // Jika batch < step, berarti sudah halaman terakhir
            if (batch.length < step) break;
            offset += step;
        }

        return allOrgs;
    } catch (error) {
        console.error("getOrganizations:", error.message);
        return [];
    }
};

/**
 * Ambil semua group/kategori CKAN dengan detail lengkap.
 * all_fields=true agar tidak perlu loop group_show per ID.
 * Kategori biasanya sedikit (<25), tidak perlu offset loop.
 * @returns {Array}
 */
export const getGroups = async () => {
    try {
        const res = await ckanApi.get(
            "/group_list?all_fields=true&include_extras=true"
        );
        return res.data?.result ?? [];
    } catch (error) {
        console.error("getGroups:", error.message);
        return [];
    }
};

/**
 * Ambil daftar dataset CKAN dengan pagination dan filter organisasi opsional.
 * @param {number}      page    - Nomor halaman (default: 1)
 * @param {number}      rows    - Jumlah item per halaman (default: 12)
 * @param {string|null} orgName - Filter by organisasi (opsional)
 * @returns {{ total: number, datasets: Array }}
 */
export const getDatasets = async (page = 1, rows = 12, orgName = null) => {
    try {
        const start = (page - 1) * rows;
        const fq    = orgName ? `&fq=organization:${orgName}` : "";
        const url   = `/package_search?start=${start}&rows=${rows}${fq}`;

        const res    = await ckanApi.get(url);
        const result = res.data?.result;

        return {
            total:    result?.count                          ?? 0,
            datasets: (result?.results ?? []).map(normalizeDataset),
        };
    } catch (error) {
        console.error("getDatasets:", error.message);
        return { total: 0, datasets: [] };
    }
};

/**
 * Ambil daftar dataset CKAN berdasarkan group/kategori dengan pagination.
 * Menggunakan fq=groups:groupName — server-side filter, efisien.
 * @param {number} page      - Nomor halaman (default: 1)
 * @param {number} rows      - Jumlah item per halaman (default: 10)
 * @param {string} groupName - Nama group/kategori (wajib)
 * @returns {{ total: number, datasets: Array }}
 */
export const getDatasetsByGroup = async (page = 1, rows = 10, groupName) => {
    if (!groupName) return { total: 0, datasets: [] };
    try {
        const start = (page - 1) * rows;
        const url   = `/package_search?start=${start}&rows=${rows}&fq=groups:${groupName}`;

        const res    = await ckanApi.get(url);
        const result = res.data?.result;

        return {
            total:    result?.count                          ?? 0,
            datasets: (result?.results ?? []).map(normalizeDataset),
        };
    } catch (error) {
        console.error("getDatasetsByGroup:", error.message);
        return { total: 0, datasets: [] };
    }
};